/**
 * THE COMMUNICATION & APPROVAL LANE.
 *
 * Brian: *"this must be a communication and approval lane for us to
 * communicate to our customers"* and *"cross reference the accounts that are
 * in production."*
 *
 * The board next door answers "where is this order". That is a back-office
 * question and it is the reason the journey read as a dead screen: a status
 * cannot tell you whether anyone is waiting on the customer. An order can sit
 * in production for eleven weeks owing nothing, or sit one day on a roster
 * whose spellings nobody has confirmed. Same status, opposite urgency.
 *
 * So this view leads with the only question that produces an action: WHO OWES
 * US SOMETHING, ON WHICH TRACK, AND WHO DO I CONTACT. The three tracks are the
 * ones that actually gate a ring build and they run in parallel — artwork to
 * JCAD, roster and sizes, financials. Nothing moves to manufacturing until all
 * three land.
 *
 * A track with nothing recorded says so. It is never shown as clear, because
 * chasing a customer who owes nothing costs more than not chasing one who does.
 */
import { useEffect, useMemo, useState } from 'react'
import { api } from '../../lib/api.js'
import { useAuth } from '../../lib/auth.jsx'
import { laneDraft } from './laneDraft.js'
import { useSessionGeneration } from '../../lib/useSessionGeneration.js'
import { createAccountFeedController, accountFeedForRequest } from './accountFeedState.js'
import { LaneMappingNotice } from './JourneyCoverageNotice.jsx'
import Timeline from './Timeline.jsx'
import { EXTERNAL_STAFF_URL } from '../../lib/surfaces.js'
import styles from './LaneBoard.module.css'

const TRACK_ORDER = ['artwork', 'roster', 'financials']

/* Same shape as the board's stat card, on purpose. Two kinds of tile for two
 * kinds of number is how one screen ends up looking like two. */
function Metric({ n, label, tone }) {
  return (
    <div className={styles.metric} data-tone={tone || ''}>
      <div className={styles.metricNum}>{n ?? '—'}</div>
      <div className={styles.metricLabel}>{label}</div>
    </div>
  )
}

const WAIT_LABEL = {
  customer: 'Customer',
  sr: 'Us',
  clear: 'Clear',
  unknown: 'Not recorded',
}

/* ClickUp owns the tracking number; this only displays it.
 *
 * The carrier is not recorded anywhere, so it is inferred from the number's
 * shape — and ONLY on patterns that cannot reasonably be anything else. A
 * wrong carrier link sends a coach to a page saying their rings do not exist,
 * which is worse than showing a number they can paste themselves. Anything
 * ambiguous stays plain text on purpose.
 */
function carrierLink(raw) {
  const s = String(raw || '').replace(/\s+/g, '').toUpperCase()
  if (!s) return null
  if (/^1Z[0-9A-Z]{16}$/.test(s)) {
    return { carrier: 'UPS', url: `https://www.ups.com/track?tracknum=${s}` }
  }
  if (/^(94|93|92|95)\d{18,20}$/.test(s)) {
    return { carrier: 'USPS', url: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${s}` }
  }
  /* NO FEDEX. Brian: "We generally ship everything with UPS. Sometimes with
   * USPS." The backend dropped FedEx shape matching on that fact; this link
   * builder had kept it, so a bare 12-digit number — the least distinctive
   * shape there is — sent a coach to a FedEx page reporting no such shipment.
   * Two carriers, and anything that isn't unmistakably one of them stays
   * plain text. */
  return null // unknown shape — show the number, do not guess a carrier
}

function fmtDate(v) {
  if (!v) return null
  const n = Number(v)
  const d = new Date(Number.isFinite(n) && n > 0 ? n : v)
  return Number.isFinite(d.getTime()) ? d.toISOString().slice(0, 10) : null
}

/* ⚠️ `mailtoFor` WAS HERE AND IS DELIBERATELY GONE. It composed a mail-client
 * link that sent the chase FROM THE REP'S OWN MAILBOX — so the coach got an
 * email that left no trace whatsoever in the Team Meeting Room they are being
 * asked to go and act in. Two consequences, both measured rather than feared:
 * the next rep to open this order could not tell whether anyone had chased it,
 * and the customer's own record of the conversation had a hole in it exactly
 * where the conversation was.
 *
 * Brian's rule settles it: anything the customer sees is EXTERNAL, and the
 * room's thread is the record. So the draft survives — it is good, it is built
 * from this order's real facts and it names precisely what is outstanding —
 * but the SEND does not happen from an internal screen.
 *
 * It is a deletion rather than a flag, for the reason deletions are used
 * elsewhere in this repo: a flag gets flipped back by somebody in a hurry, and
 * a function that does not exist cannot be re-wired to a button by accident. */

export default function LaneBoard({ rep }) {
  const { user } = useAuth()
  const [read, setRead] = useState(null)
  const generation = useSessionGeneration()
  const [phase, setPhase] = useState('')
  const [onlyWaiting, setOnlyWaiting] = useState(true)
  const [openId, setOpenId] = useState(null)
  const [page,setPage]=useState(0)
  useEffect(()=>setPage(0),[phase,onlyWaiting,rep])

  const requestKey = JSON.stringify([phase, onlyWaiting, rep || '', generation])
  const feed = accountFeedForRequest(read, requestKey)
  const data = feed.status === 'done' ? feed.payload : null
  const err = feed.status === 'error' || feed.status === 'unavailable' ? feed.error : null
  const loading = feed.status === 'loading'
  useEffect(() => {
    const query = { ...(phase ? {phase} : {}), ...(onlyWaiting ? {waiting: 'customer'} : {}), ...(rep ? {rep} : {}) }
    const controller = createAccountFeedController({
      sources: [{key: 'journey-lane', label: 'Customer-approval lane', path: '/api/journey/lane', call: opts => api.journeyLane(query, opts), normalize: result => result.orders}],
      onChange: state => setRead({...state, requestKey}),
    })
    controller.load()
    return () => controller.dispose()
  }, [phase, onlyWaiting, rep, requestKey])

  const orders = data?.orders || []
  const currentPage=Math.min(page,Math.max(0,Math.ceil(orders.length/25)-1))

  const cov = data?.coverage
  const thinTracks = useMemo(() => {
    if (!cov?.orders) return []
    return TRACK_ORDER.filter((k) => (cov[k] ?? 0) / cov.orders < 0.5)
      .map((k) => ({ key: k, pct: Math.round(((cov[k] ?? 0) / cov.orders) * 100) }))
  }, [cov])

  /* The standing caveats, collected. Each one is a real limit on what this
   * lane can claim, so none is dropped — they are folded, not hidden, and the
   * count is on the summary line whether or not anyone opens it. */
  const gaps = useMemo(() => {
    const out = []
    if (data?.actualDeliveryField && !data.actualDeliveryField.id) {
      out.push({
        title: 'No Actual Delivery Date field on the order list.',
        body: data.actualDeliveryField.needed,
      })
    }
    if (thinTracks.length > 0) {
      out.push({
        title: `Thin data on ${thinTracks.map((t) => t.key).join(' and ')}.`,
        body: `${thinTracks.map((t) => `${t.key} is recorded on ${t.pct}% of live orders`).join('; ')}. `
          + 'Orders with nothing recorded are marked “not recorded” rather than counted as clear, so '
          + 'this lane under-reports rather than sending someone to chase a customer who owes nothing.',
      })
    }
    return out
  }, [data, thinTracks])

  return (
    <div className={styles.wrap}>
      {/* ONE control bar. The view had a phase pill row, a checkbox and a
        * separate rep bar stacked as three rows of chrome before a single
        * order appeared. */}
      <div className={styles.filters}>
        <div className={styles.seg}>
          {['', 'Production', 'Approvals', 'Delivery', 'New'].map((p) => (
            <button
              key={p || 'all'}
              type="button"
              className={`${styles.segBtn} ${phase === p ? styles.segOn : ''}`}
              onClick={() => setPhase(p)}
            >
              {p || 'All live'}
            </button>
          ))}
        </div>
        <label className={styles.check}>
          <input
            type="checkbox"
            checked={onlyWaiting}
            onChange={(e) => setOnlyWaiting(e.target.checked)}
          />
          Only where the customer owes us
        </label>
      </div>

      {data && <LaneMappingNotice scope={data.scope} returnedRows={orders.length} />}

      {/* ONE summary, counting the set actually on screen.
        *
        * This was a text tally and a clock bar, two rows of run-together
        * numbers sitting under four stat cards that counted a different set
        * entirely. Same shape as the board's cards, because it is the same
        * kind of fact — and the clock belongs here, not in its own strip:
        * "waiting on the customer" and "past 16 weeks" are read together or
        * they are not read at all. */}
      {data && (
        <div className={styles.summary}>
          <Metric n={data.count} label="Shown" />
          <Metric n={data.waitingOnCustomer} label="Waiting on the customer" tone="warn" />
          {/* Zero is not good news, so it does not get the good-news colour.
            * A green 0 under "all three tracks clear" reads as a pass at a
            * glance when it is the opposite. */}
          <Metric n={data.readyToBuild} label="All three tracks clear" tone={data.readyToBuild ? 'ok' : 'mute'} />
          {data.clock && (
            <>
              <Metric n={data.clock.building} label="In build" />
              <Metric n={data.clock.due} label="Due now" tone={data.clock.due ? 'warn' : ''} />
              <Metric n={data.clock.overdue} label="Past 16 weeks" tone={data.clock.overdue ? 'bad' : ''} />
              <Metric n={data.clock.notStarted} label="Clock not started" tone="mute" />
              {data.clock.beatTheWindow > 0 && (
                <Metric n={data.clock.beatTheWindow} label="Beat the window" tone="ok" />
              )}
            </>
          )}
        </div>
      )}

      {/* The exceptions, and only when there are any. A zero here is noise;
        * a non-zero is a conversation owed today. */}
      {data && (data.stalled > 0 || data.clock?.cannotMeetRequest > 0 || data.clock?.approvedButUndated > 0) && (
        <div className={styles.flags}>
          {data.clock?.cannotMeetRequest > 0 && (
            <span className={styles.flagBad}>
              <b>{data.clock.cannotMeetRequest}</b> asked for a date we cannot hit
            </span>
          )}
          {data.clock?.approvedButUndated > 0 && (
            <span className={styles.flagBad}>
              <b>{data.clock.approvedButUndated}</b> approved but undated
            </span>
          )}
          {data.stalled > 0 && (
            <span className={styles.flag}><b>{data.stalled}</b> nothing recorded in 2 weeks</span>
          )}
        </div>
      )}

      {/* THE CAVEATS. Both are true and both matter, but they are standing
        * conditions rather than today's news, and as two full-width boxes
        * they pushed every actual order below the fold. Folded to one line
        * that still states the count, and still opens. */}
      {(gaps.length > 0) && (
        <details className={styles.gaps}>
          <summary className={styles.gapsSummary}>
            {gaps.length === 1 ? gaps[0].title : `${gaps.length} data gaps affect what this lane can tell you`}
          </summary>
          <div className={styles.gapsBody}>
            {gaps.map((g) => (
              <p key={g.title}><strong>{g.title}</strong> {g.body}</p>
            ))}
          </div>
        </details>
      )}

      {err && <div className={styles.error}><strong>Customer-approval lane unavailable, not empty.</strong> {err}</div>}
      {loading && <div className={styles.empty}>Reading the order board…</div>}

      {!loading && !err && orders.length === 0 && (
        <div className={styles.empty}>
          <strong>Nobody is waiting on a customer here.</strong>
          <br />
          That is a real answer, not an empty screen. Widen the phase filter or untick the box to
          see everything live.
        </div>
      )}

      <div className={styles.list}>
        {orders.slice(currentPage*25,currentPage*25+25).map((o) => {
          const open = openId === o.orderId
          return (
            <article key={o.orderId} className={styles.card} data-waiting={o.waitingOnCustomer ? '1' : '0'}>
              <div className={styles.head}>
                <div className={styles.titleBlock}>
                  <div className={styles.name}>
                    {o.url ? <a href={o.url} target="_blank" rel="noopener noreferrer">{o.name}</a> : o.name}
                  </div>
                  <div className={styles.meta}>
                    <span className={styles.phase}>{o.phase}</span>
                    <span>{o.status}</span>
                    {o.reps?.length > 0 && <span>· {o.reps.join(', ')}</span>}
                  </div>
                </div>
                {o.allClear && <span className={styles.ready}>Ready to build</span>}
              </div>

              <details className={styles.orderDetails}><summary>Open order details and next action</summary>
              <div className={styles.tracks}>
                {TRACK_ORDER.map((key) => {
                  const t = o.tracks.find((x) => x.key === key)
                  if (!t) return null
                  return (
                    <div key={key} className={styles.track} data-w={t.waiting}>
                      <span className={styles.trackLabel}>{t.label}</span>
                      <span className={styles.trackState}>{t.state}</span>
                      <span className={styles.trackWho}>{WAIT_LABEL[t.waiting]}</span>
                    </div>
                  )
                })}
              </div>

              {/* DELIVERY. ClickUp owns these fields; the journey surfaces
                * them so a rep answering "where are our rings" does not have
                * to open ClickUp to find out. */}
              {(o.tracking || o.inHands || o.deliveryType) && (
                <div className={styles.delivery}>
                  <span className={styles.deliveryLabel}>Delivery</span>
                  {o.deliveryType && <span>{String(o.deliveryType).trim()}</span>}
                  {o.tracking && (() => {
                    const c = carrierLink(o.tracking)
                    return c ? (
                      <a className={styles.trackLink} href={c.url} target="_blank" rel="noopener noreferrer">
                        {c.carrier} {o.tracking}
                      </a>
                    ) : (
                      <span className={styles.trackPlain} title="Carrier not recorded and the number's format is ambiguous, so it is not linked">
                        {o.tracking}
                      </span>
                    )
                  })()}
                  {fmtDate(o.inHands)
                    ? <span className={styles.inHands}>In hands {fmtDate(o.inHands)}</span>
                    : o.tracking && <span className={styles.noDate}>no in-hands date recorded</span>}
                </div>
              )}

              {o.customerOwes?.length > 0 && (
                <div className={styles.owes}>
                  {o.customerOwes.map((c) => (
                    <div key={c.track} className={styles.oweRow}>
                      <strong>{c.label}:</strong> {c.ask || c.state}
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.actions}>
                {o.contact?.name || o.contact?.email ? (
                  <span className={styles.contact}>
                    {o.contact.name || 'contact'}{o.contact.email ? ` · ${o.contact.email}` : ' · no email on the order'}
                  </span>
                ) : (
                  <span className={styles.noContact}>No contact recorded on this order</span>
                )}

                {/* THE SEND HAPPENS IN THE ROOM, NOT FROM HERE. The condition
                  * is no longer "do we have an email address" — it is "does
                  * the customer owe us something", because the thread does not
                  * need an address to reach them. */}
                {o.waitingOnCustomer && (
                  <a
                    className={styles.btn}
                    href={EXTERNAL_STAFF_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Message them in the room ↗
                  </a>
                )}

                <button
                  type="button"
                  className={`${styles.btn} ${styles.ghost}`}
                  onClick={() => setOpenId(open ? null : o.orderId)}
                >
                  {open ? 'Hide draft' : 'Draft to paste'}
                </button>
              </div>

              {/* THE DATES. Always visible, not behind a click — Brian:
                * "visually this has to be very clear." A timeline you have to
                * open is a timeline nobody reads. */}
              <Timeline timeline={o.timeline} />

              {open && (
                <>
                  <p className={styles.draftNote}>
                    Paste this into <strong>{o.name}</strong>’s thread in the Team Meeting Room, so
                    the chase is on the customer’s own record. Sending it from your mailbox instead
                    leaves the room — the place they are being asked to act — showing nothing.
                  </p>
                  <pre className={styles.preview}>{laneDraft(o, user).available ? laneDraft(o, user).body : `Draft unavailable: ${laneDraft(o, user).missing.join('; ')}`}</pre>
                </>
              )}
              </details>
            </article>
          )
        })}
      </div>
      {orders.length>25&&<div className={styles.actions}>
        <button className={styles.btn} disabled={currentPage===0} onClick={()=>setPage(currentPage-1)}>Previous</button>
        <span>{currentPage*25+1}-{Math.min((currentPage+1)*25,orders.length)} of {orders.length} orders</span>
        <button className={styles.btn} disabled={(currentPage+1)*25>=orders.length} onClick={()=>setPage(currentPage+1)}>Next</button>
      </div>}
    </div>
  )
}
